'use client';

import { Calendar, CommentEmptyCloud, Flame, MenuBulletHorizontal } from '@zoomit/icons';
import { Sheet } from '@zoomit/mobile-sheet';
import { Box, Flex, IconButton, Image, Link, Spacing } from '@zoomit/primitives';
import { cssVar, TextIcon, theme, Typography } from '@zoomit/zoomit-ds';

import { isMobile } from '../../constants/media-query';
import { useOrigin } from '../../providers/OriginProvider';
import type { ArticleCardResponse } from '../../types/articles';
import MoreArticleOptions from '../modules/shared/MoreArticleOptions';
import ExternalIcon from '../shared/ExternalIcon';
import RelativeTime from '../shared/RelativeTime';
import EllipticBox from './EllipticBox';

type Props = {
  article: ArticleCardResponse;
  handleEvent?: (clickedPosition: string) => void;
  onBookmarksRefetch?: () => void;
};

const ArticleCard = (props: Props) => {
  const { article, handleEvent, onBookmarksRefetch } = props;
  const { baseUrl, baseMediaUrl, isSameOrigin, originUrl } = useOrigin();

  const { DesktopMoreArticleOptions, MobileMoreArticleOptions, toggleMoreOptions, isMoreOptionsOpen } =
    MoreArticleOptions({
      articleId: article.id,
      title: article.title,
      url: article.type === 'External' ? article.link : `${originUrl}/${article.slug}/`,
      onBookmarksRefetch,
      hasBookmark: isSameOrigin,
    });

  const linkAttr = {
    href: article.type === 'External' ? article.link : `${baseUrl}/${article.slug}/`,
    rel: article.type === 'External' ? (article.linkIsFollow ? undefined : 'nofollow') : undefined,
    target: article.type === 'External' ? '_blank' : undefined,
    prefetch: article.type === 'External' ? false : true,
  };

  return (
    <div className="bg-card relative mb-2 max-w-[273px] min-w-[273px] overflow-hidden rounded-sm shadow md:max-w-full md:min-w-auto">
      <Link {...linkAttr} onClick={() => handleEvent?.('image')}>
        <div className="aspect-primary relative w-full">
          <Image
            src={`${baseMediaUrl}/${article.coverImageLink.fileName}-${article.coverImageLink.id}`}
            placeholder="blur"
            blurDataURL={article.coverImageLink.preview}
            alt={article.coverImageLink.alt}
            sizes="320px"
            fill
            className="object-cover"
          />
        </div>
      </Link>
      {article.isAdvertisement && (
        <EllipticBox backgroundColor={cssVar(theme.primary1)} position="top-left">
          <Typography variant="subhead" textColor={cssVar(theme.white)}>
            تبلیغات
          </Typography>
        </EllipticBox>
      )}
      {article.type === 'External' && (
        <EllipticBox position="top-left">
          <ExternalIcon
            hegith={12}
            sponsorName={article.sponsorName}
            sponsorLogo={article.sponsorLogo}
            variant={'elliptic'}
          />
        </EllipticBox>
      )}

      <div className="flex min-h-[132px] w-full flex-col justify-between gap-4 px-6 pt-6 pb-4">
        <Flex gap={Spacing.x2} flexWrap="nowrap" alignItems={'flex-start'}>
          {article.type === 'Internal' && article.isTrend && (
            <Flame style={{ flexShrink: 0, height: 24 }} fill={cssVar(theme.primary1)} />
          )}
          <Link {...linkAttr} className="**:hover:text-text-hover" onClick={() => handleEvent?.('title')}>
            <Typography className="line-clamp-2" variant="h6" tag="div" lineHeight={2}>
              {article.title}
            </Typography>
          </Link>
        </Flex>
        <Flex justifyContent="space-between">
          <Flex gap={1.6}>
            {article.type === 'Internal' && (
              <TextIcon
                rightIcon={<CommentEmptyCloud stroke={cssVar(theme.grey1)} strokeWidth={1.7} size="sm" />}
                onClick={() => handleEvent?.('icon')}
              >
                <Typography
                  variant="subhead"
                  className="fa"
                  textColor={cssVar(theme.grey1)}
                  suppressHydrationWarning
                >
                  {article.totalDiscussCount}
                </Typography>
              </TextIcon>
            )}
            <TextIcon
              rightIcon={<Calendar className="!fill-grey1" size="sm" />}
              onClick={() => handleEvent?.('icon')}
            >
              <Typography className="fa" variant="subhead" textColor={cssVar(theme.grey1)}>
                <RelativeTime time={article.publishedDate} timeAgoBasis="one-month" />
              </Typography>
            </TextIcon>
          </Flex>
          <Box>
            {!isMobile ? (
              <DesktopMoreArticleOptions placement="right-start">
                <IconButton
                  touchTargetInset={isMobile ? 16 : 8}
                  onClick={(e) => {
                    toggleMoreOptions(e);
                    handleEvent?.('toolbar');
                  }}
                >
                  <MenuBulletHorizontal fill={cssVar(theme.grey3)} size="md" />
                </IconButton>
              </DesktopMoreArticleOptions>
            ) : (
              <>
                <IconButton
                  touchTargetInset={isMobile ? 16 : 8}
                  onClick={(e) => {
                    toggleMoreOptions(e);
                    handleEvent?.('toolbar');
                  }}
                >
                  <MenuBulletHorizontal fill={cssVar(theme.grey3)} size="md" />
                </IconButton>
                <Sheet isOpen={isMoreOptionsOpen} onClose={toggleMoreOptions}>
                  <Sheet.Container>
                    <Sheet.CloseIndicator />
                    <MobileMoreArticleOptions />
                  </Sheet.Container>
                  <Sheet.Backdrop />
                </Sheet>
              </>
            )}
          </Box>
        </Flex>
      </div>
    </div>
  );
};

export default ArticleCard;
